import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';

import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';
import { fmtDateTime } from './format';

// „Offen“-Leisten: Aufgaben stehen dort, wo sie hingehören – am Lead, am Termin und im Gebiet. Der
// Posteingang ist nur die Summe dieser offenen Punkte.

function Bar({ parts }: { parts: React.ReactNode[] }) {
  if (parts.length === 0) return <p className="open-items open-items-none">Nichts offen.</p>;
  return (
    <p className="open-items" role="status">
      <strong>Offen:</strong>{' '}
      {parts.map((p, i) => (
        <span key={i}>
          {i > 0 && ' · '}
          {p}
        </span>
      ))}
    </p>
  );
}

function useOpenTickets(q: { lead_id?: string; appointment_id?: string }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const query = { ...q, status: ['open'], assigned: 'all' };
  return useQuery({ queryKey: [...projectKeys.tickets(project.id, query), 'count'], queryFn: ({ signal }) => papi.tickets({ ...query, limit: 50 }, signal) });
}

export function LeadOpenItems({ leadId }: { leadId: string }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const followUps = useQuery({
    queryKey: projectKeys.followUps(project.id, { lead_id: leadId, assigned: 'all', status: 'open' }),
    queryFn: ({ signal }) => papi.followUps({ lead_id: leadId, assigned: 'all', status: 'open' }, signal),
    enabled: can('task.read'),
  });
  const tickets = useOpenTickets({ lead_id: leadId });
  const parts: React.ReactNode[] = [];
  for (const f of followUps.data?.items ?? []) {
    parts.push(
      <span className={f.overdue ? 'badge badge-error' : undefined}>
        {f.type === 'CALLBACK' ? 'Rückruf' : 'Wiedervorlage'} {fmtDateTime(f.due_at)}
        {f.assigned_user_name ? ` (${f.assigned_user_name})` : ''}
      </span>,
    );
  }
  const n = tickets.data?.items.length ?? 0;
  if (n > 0) parts.push(<a href="#rueckfragen">{n === 1 ? '1 Rückfrage' : `${n}${tickets.data?.has_more ? '+' : ''} Rückfragen`}</a>);
  if (followUps.isPending && tickets.isPending) return null;
  return <Bar parts={parts} />;
}

export function AppointmentOpenItems({ appointmentId }: { appointmentId: string }) {
  const tickets = useOpenTickets({ appointment_id: appointmentId });
  const n = tickets.data?.items.length ?? 0;
  if (tickets.isPending) return null;
  return <Bar parts={n > 0 ? [<a href="#rueckfragen">{n === 1 ? '1 Rückfrage' : `${n} Rückfragen`}</a>] : []} />;
}

export function AreaOpenItems({ areaId }: { areaId: string }) {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const allowed = can('ticket.manage') || can('call.review');
  const team = useQuery({ queryKey: projectKeys.team(project.id), queryFn: ({ signal }) => papi.teamOverview(signal), enabled: allowed });
  const row = team.data?.areas.find((a) => a.area_id === areaId);
  if (!allowed || !row) return null;
  const base = `/p/${project.key}`;
  const n = new Intl.NumberFormat('de-DE');
  return (
    <Bar
      parts={[
        row.field_sales_user_id ? (
          <>
            Makler <Link to={`${base}/makler/${row.field_sales_user_id}`}>{row.field_sales_name}</Link>
          </>
        ) : (
          <span className="badge badge-warn">kein Makler zugeordnet</span>
        ),
        <Link to={`${base}/leads?area_id=${areaId}&status=NEW`}>{n.format(row.new_leads)} noch nicht angerufen</Link>,
        <Link to={`${base}/termine?ansicht=kalender&area=${areaId}`}>{row.appointments_next_7_days} Termine (7 Tage)</Link>,
        <Link to={`${base}/posteingang?ansicht=rueckfragen&gebiet=${areaId}`}>{row.open_tickets} offene Rückfragen</Link>,
      ]}
    />
  );
}
